import React from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { plans, getProductFamily } from "../PricingSectionCommon";

function BillingSelector({ product, selectedPlan, onPlanChange }) {
  const family = getProductFamily(product);

  if (!family) {
    return null;
  }

  const monthlyPlan = plans.find(p => p.id === family.monthly);
  const annualPlan = plans.find(p => p.id === family.annual);

  if (!monthlyPlan || !annualPlan) {
    return null;
  }

  // Calculate savings
  const monthlyPrice = parseFloat(String(monthlyPlan.price).replace(/,/g, ""));
  const annualPrice = parseFloat(String(annualPlan.price).replace(/,/g, ""));
  const annualAsMonthly = annualPrice / 12;
  const savingsPerMonth = monthlyPrice - annualAsMonthly;
  const savingsPerYear = savingsPerMonth * 12;
  const freeMonths = Math.round(savingsPerYear / monthlyPrice);

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-4">Choose Your Plan</h3>

      <div className="space-y-3">
        {/* Annual Option - Show first as recommended */}
        <label
          className={`relative block p-4 rounded-lg border-2 cursor-pointer transition-all ${
            selectedPlan === family.annual
              ? "border-green-600 bg-green-50"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <input
            type="radio"
            name="billing"
            value={family.annual}
            checked={selectedPlan === family.annual}
            onChange={() => onPlanChange(family.annual)}
            className="sr-only"
          />
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Annual</span>
              <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                SAVE {freeMonths} MONTHS
              </span>
            </div>
            {selectedPlan === family.annual && (
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            )}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">{formatPrice(annualPrice)}</span>
            <span className="text-gray-500">/year</span>
          </div>
          <p className="text-green-600 text-sm mt-1">
            {formatPrice(annualAsMonthly)}/mo — Save {formatPrice(savingsPerYear)}/yr
          </p>
        </label>

        {/* Monthly Option */}
        <label
          className={`relative block p-4 rounded-lg border-2 cursor-pointer transition-all ${
            selectedPlan === family.monthly
              ? "border-green-600 bg-green-50"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <input
            type="radio"
            name="billing"
            value={family.monthly}
            checked={selectedPlan === family.monthly}
            onChange={() => onPlanChange(family.monthly)}
            className="sr-only"
          />
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-gray-900">Monthly</span>
            {selectedPlan === family.monthly && (
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            )}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">{formatPrice(monthlyPrice)}</span>
            <span className="text-gray-500">/month</span>
          </div>
          <p className="text-gray-500 text-sm mt-1">
            Flexible month-to-month
          </p>
        </label>
      </div>

      {/* Upsell message when monthly is selected */}
      {selectedPlan === family.monthly && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>Switch to annual</strong> and get {freeMonths} months free —
            save {formatPrice(savingsPerYear)} per year!
          </p>
        </div>
      )}
    </div>
  );
}

export default BillingSelector;
